import {
  DEFAULT_API_BASE_URL,
  createContractAnalyzerAppApiClient,
} from '@contract-analyzer/api-client';

import { publishSessionDeadline, requestAuthRecovery } from '@/lib/auth-events';
import { consumeUserActivity } from '@/lib/user-activity';

function normalizeApiBaseUrl(baseUrl: string): string {
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
}

function normalizeBasePath(basePath: string): string {
  return basePath.replace(/\/+$/, '');
}

function readAbsoluteUrl(baseUrl: string): URL | null {
  try {
    return new URL(baseUrl);
  } catch {
    return null;
  }
}

function prefixWithBasePath(path: string, basePath: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const normalizedBasePath = normalizeBasePath(basePath);
  return normalizeApiBaseUrl(
    normalizedBasePath ? `${normalizedBasePath}${normalizedPath}` : normalizedPath,
  );
}

export function resolveApiBaseUrl(
  configuredBaseUrl: string | undefined,
  basePath: string = import.meta.env.BASE_URL,
): string {
  const trimmedBaseUrl = configuredBaseUrl?.trim();
  if (!trimmedBaseUrl) {
    return prefixWithBasePath(DEFAULT_API_BASE_URL, basePath);
  }

  const absoluteUrl = readAbsoluteUrl(trimmedBaseUrl);
  if (absoluteUrl !== null) {
    if (absoluteUrl.protocol !== 'https:') {
      throw new Error('VITE_API_BASE_URL must use HTTPS when configured as an absolute URL.');
    }
    return normalizeApiBaseUrl(absoluteUrl.toString());
  }

  if (trimmedBaseUrl.startsWith('/')) {
    return normalizeApiBaseUrl(trimmedBaseUrl);
  }

  const normalizedConfiguredBaseUrl = `/${trimmedBaseUrl}`;
  const normalizedBasePath = normalizeBasePath(basePath);
  if (
    normalizedBasePath.length === 0 ||
    normalizedConfiguredBaseUrl === normalizedBasePath ||
    normalizedConfiguredBaseUrl.startsWith(`${normalizedBasePath}/`)
  ) {
    return normalizeApiBaseUrl(normalizedConfiguredBaseUrl);
  }

  return normalizeApiBaseUrl(`${normalizedBasePath}${normalizedConfiguredBaseUrl}`);
}

export const apiBaseUrl = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL);

export const apiRedocUrl = `${apiBaseUrl}/public/redoc`;

/**
 * The original file, streamed by the API with the session cookie. Built here, not taken
 * from a preview's `viewer_url`: the API cannot see the web's base path or the
 * ingress's scheme, so its own absolute link can point at the wrong place.
 */
export function documentDownloadUrl(documentId: string): string {
  return `${apiBaseUrl}/documents/${encodeURIComponent(documentId)}/download`;
}

export const apiClient = createContractAnalyzerAppApiClient({
  baseUrl: apiBaseUrl,
  onAuthFailure: requestAuthRecovery,
  onSessionExpiresAt: publishSessionDeadline,
  isUserActive: consumeUserActivity,
});
